import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanLine, ArrowRight, AlertTriangle, CheckCircle2, X, ExternalLink } from "lucide-react";
import RejectionDialog from "@/components/RejectionDialog";

export default function ScanMode() {
  const [code, setCode] = useState("");
  const [hit, setHit] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const inputRef = useRef(null);

  const focusInput = useCallback(() => inputRef.current?.focus(), []);
  useEffect(() => { focusInput(); }, [focusInput]);

  const lookup = async (e) => {
    e.preventDefault();
    const value = code.trim();
    if (!value) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/scan/${encodeURIComponent(value)}`);
      setHit(data);
      setRecent((r) => [{ ...data, at: new Date().toISOString() }, ...r.filter((x) => x.case_id !== data.case_id)].slice(0, 8));
    } catch (err) {
      setHit(null);
      toast.error(apiError(err));
    } finally {
      setCode("");
      setBusy(false);
      focusInput();
    }
  };

  const advance = async () => {
    if (!hit?.next_stage) return;
    setBusy(true);
    try {
      await api.post(`/cases/${hit.case_id}/status`, { status: hit.next_stage, note: "Advanced from Scan Mode" });
      toast.success(`${hit.accession_number} → ${hit.next_stage}`);
      const { data } = await api.get(`/scan/${encodeURIComponent(hit.specimen?.barcode || hit.accession_number)}`);
      setHit(data);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); focusInput(); }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100" data-testid="scan-mode-page">
      <div className="mx-auto max-w-3xl">
        <header className="flex flex-wrap items-center gap-3">
          <ScanLine size={26} className="text-teal-300" />
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Scan Mode</h1>
          <Link to="/" data-testid="scan-exit" className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-slate-300 hover:text-white">
            <X size={16} /> Exit
          </Link>
        </header>
        <p className="mt-1 text-sm text-slate-400">
          Scan a cassette, slide, accession or courier packing-slip label. The gun types the code
          and presses Enter for you.
        </p>

        <form onSubmit={lookup} className="mt-5">
          <Input ref={inputRef} value={code} onChange={(e) => setCode(e.target.value)} autoFocus
                 data-testid="scan-input" inputMode="text" autoComplete="off"
                 placeholder="Scan or type a barcode…"
                 className="h-20 border-2 border-teal-400/60 bg-slate-900 px-5 text-2xl font-mono tracking-wider text-white placeholder:text-slate-500 focus-visible:ring-teal-400" />
        </form>

        {hit && (
          <section className="mt-5 border-2 border-slate-700 bg-slate-900 p-5" data-testid="scan-result">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-3xl font-bold text-teal-300" data-testid="scan-accession">
                {hit.accession_number}
              </span>
              {hit.priority === "STAT" && (
                <span className="bg-red-600 px-2 py-0.5 text-sm font-bold uppercase">STAT</span>
              )}
              {hit.voided && (
                <span className="inline-flex items-center gap-1 bg-red-800 px-2 py-0.5 text-sm font-bold uppercase">
                  <AlertTriangle size={14} /> voided
                </span>
              )}
              <Link to={`/cases/${hit.case_id}`} data-testid="scan-open-case"
                    className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-teal-300 hover:text-white">
                Open case <ExternalLink size={14} />
              </Link>
            </div>
            <p className="mt-1 text-xl font-semibold" data-testid="scan-patient">{hit.patient_name}</p>
            <p className="text-sm text-slate-400">
              {hit.patient_mrn} · {hit.specimen_type}{hit.specimen_site ? ` — ${hit.specimen_site}` : ""}
              {hit.hours_open !== null ? ` · open ${hit.hours_open} h` : ""}
            </p>

            {hit.specimen && (
              <p className="mt-3 border-l-4 border-teal-400 bg-slate-800 px-3 py-2 text-sm" data-testid="scan-specimen">
                <span className="font-mono font-bold">{hit.specimen.barcode}</span> — {hit.specimen.kind}
                {hit.specimen.label ? ` ${hit.specimen.label}` : ""}{hit.specimen.stain ? ` · ${hit.specimen.stain}` : ""}
                {hit.specimen.checked_out ? ` · checked out to ${hit.specimen.checked_out_to}` : ""}
                {hit.specimen.disposed ? " · DISPOSED" : ""}
              </p>
            )}

            {hit.sendout && (
              <div className="mt-3 border-2 border-sky-500 bg-sky-950/50 p-3" data-testid="scan-sendout">
                <p className="text-base font-bold text-sky-200">
                  SEND-OUT {hit.sendout.slip_number || "(no slip number)"} — {hit.sendout.sendout_status}
                </p>
                <p className="mt-1 text-sm text-sky-100">
                  {hit.sendout.destination_lab || "Destination to be arranged"}
                  {hit.sendout.test_requested ? ` · ${hit.sendout.test_requested}` : ""}
                  {` · ${hit.sendout.material_count} item(s)`}
                </p>
                {(hit.sendout.carrier || hit.sendout.tracking_number) && (
                  <p className="mt-1 font-mono text-xs text-sky-200/90">
                    {hit.sendout.carrier} {hit.sendout.tracking_number}
                  </p>
                )}
                <p className="mt-1 text-xs text-sky-200/80">
                  Materials {hit.sendout.materials_returned ? "returned" : "still out"} · results{" "}
                  {hit.sendout.results_received ? "received" : "pending"}
                </p>
                <Link to="/sendouts" data-testid="scan-open-sendouts"
                      className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-sky-300 hover:text-white">
                  Open Send-Outs <ExternalLink size={14} />
                </Link>
              </div>
            )}

            {hit.rejection_hold && (
              <div className="mt-3 border-2 border-amber-400 bg-amber-950/60 p-3" data-testid="scan-rejection-hold">
                <p className="text-base font-bold text-amber-200">
                  HELD — {hit.rejection_hold.rejection_number}: {hit.rejection_hold.discrepancy_type}
                </p>
                <p className="mt-1 text-sm text-amber-100">{hit.rejection_hold.description}</p>
                <p className="mt-1 text-xs text-amber-200/80">
                  Resolve the discrepancy in CAPTraX before this case can move.
                </p>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="scan-stages">
              {hit.stages.map((s, i) => (
                <span key={s} className={`px-2 py-1 text-xs font-bold uppercase ${
                  i < hit.stage_index ? "bg-slate-700 text-slate-300"
                    : i === hit.stage_index ? "bg-teal-400 text-slate-950"
                    : "bg-slate-800 text-slate-500"}`}>
                  {s}
                </span>
              ))}
            </div>
            <p className="mt-2 text-sm text-slate-400">
              {hit.blocks} block(s) · {hit.slides} slide(s) · report {hit.report_status}
            </p>

            {hit.next_stage ? (
              hit.can_advance && !hit.voided && !hit.rejection_hold && (
                <Button onClick={advance} disabled={busy} data-testid="scan-advance-button"
                        className="mt-4 h-20 w-full bg-teal-500 text-xl font-bold text-slate-950 hover:bg-teal-400">
                  {busy ? "Saving…" : <>Advance to {hit.next_stage} <ArrowRight size={22} className="ml-2" /></>}
                </Button>
              )
            ) : (
              <p className="mt-4 inline-flex items-center gap-2 text-base font-semibold text-teal-300" data-testid="scan-final-stage">
                <CheckCircle2 size={18} /> Final stage — {hit.status}
              </p>
            )}
            {hit.next_stage === "Sign-Out" && (
              <p className="mt-2 text-xs text-slate-400">Only a pathologist or admin can sign a case out.</p>
            )}
            {!hit.voided && (
              <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="scan-log-rejection"
                      className="mt-3 h-14 w-full border-amber-500/60 bg-transparent text-base font-semibold text-amber-300 hover:bg-amber-500/10">
                <AlertTriangle size={18} className="mr-2" /> Log a specimen discrepancy
              </Button>
            )}
          </section>
        )}

        {recent.length > 0 && (
          <section className="mt-6" data-testid="scan-recent">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">This session</h2>
            <ul className="mt-2 divide-y divide-slate-800 border border-slate-800">
              {recent.map((r) => (
                <li key={r.case_id + r.at} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
                    data-testid={`scan-recent-${r.accession_number}`}>
                  <span className="font-mono font-semibold text-teal-300">{r.accession_number}</span>
                  <span className="text-slate-300">{r.patient_name}</span>
                  <span className="ml-auto text-xs text-slate-500">{r.status} · {fmtDateTime(r.at)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <RejectionDialog open={rejectOpen} onOpenChange={setRejectOpen} caseId={hit?.case_id || ""}
                       accession={hit?.accession_number || ""}
                       onLogged={async () => {
                         if (!hit) return;
                         const { data } = await api.get(`/scan/${encodeURIComponent(hit.specimen?.barcode || hit.accession_number)}`);
                         setHit(data);
                       }} />
    </div>
  );
}
